import { useState, useMemo } from "react";
import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
} from "@hugeicons/core-free-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { createProjectsApi } from "@/lib/api/projects";
import { useTransactions } from "@/lib/hooks/use-credits";
import { cn } from "@/lib/utils";
import type { TransactionResponse } from "@/lib/api/credits";
import type { PaginatedProjectList } from "@/lib/api/projects";

/* ────────────────── Constants ──────────────────────────────────── */

const TYPE_FILTERS = [
  { label: "All types", value: "all" },
  { label: "Text", value: "llm_text" },
  { label: "Agent", value: "agent_node" },
  { label: "Image", value: "image_gen,image_upscaler" },
  { label: "Video", value: "video_gen" },
  { label: "Audio", value: "audio_tts,audio_music" },
  { label: "Transform", value: "background_remover" },
] as const;

const MODULE_TYPE_LABELS: Record<string, string> = {
  llm_text: "Text",
  agent_node: "Agent",
  image_gen: "Image",
  image_upscaler: "Image",
  video_gen: "Video",
  audio_tts: "Speech",
  audio_music: "Music",
  background_remover: "Transform",
};

const PRICING_UNIT_LABELS: Record<string, string> = {
  per_1k_tokens: "LLM",
  per_image: "Image",
  per_second: "Media",
  per_1k_chars: "Speech",
  per_request: "Transform",
};

const MODULE_TYPE_COLORS: Record<string, string> = {
  Text: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  Agent: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  LLM: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  Image: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  Video: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  Speech: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  Music: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  Media: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  Transform: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
};

const PAGE_SIZE = 20;

/* ────────────────── Helpers ────────────────────────────────────── */

function getTypeLabel(tx: TransactionResponse): string {
  if (tx.module_type && MODULE_TYPE_LABELS[tx.module_type]) {
    return MODULE_TYPE_LABELS[tx.module_type];
  }
  if (tx.pricing_unit && PRICING_UNIT_LABELS[tx.pricing_unit]) {
    return PRICING_UNIT_LABELS[tx.pricing_unit];
  }
  if (tx.amount_credits > 0) return "Grant";
  return "Other";
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatCredits(amount: number): string {
  const abs = Math.abs(amount);
  const prefix = amount < 0 ? "-" : "+";
  return `${prefix}${abs % 1 === 0 ? abs.toLocaleString() : abs.toFixed(1)}`;
}

/* ────────────────── Skeleton Loader ───────────────────────────── */

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-28" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-12" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

/* ────────────────── Transaction Row ───────────────────────────── */

interface TransactionRowProps {
  tx: TransactionResponse;
}

function TransactionRow({ tx }: TransactionRowProps) {
  const typeLabel = getTypeLabel(tx);
  const colorClass = MODULE_TYPE_COLORS[typeLabel] ?? "";
  const isDeduction = tx.amount_credits < 0;

  return (
    <TableRow>
      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
        {formatDate(tx.created_at)}
      </TableCell>
      <TableCell className="max-w-[160px] truncate text-sm">
        {tx.project_name ?? "\u2014"}
      </TableCell>
      <TableCell>
        {typeLabel !== "Other" && typeLabel !== "Grant" ? (
          <Badge
            variant="secondary"
            className={cn("text-xs font-medium", colorClass)}
          >
            {typeLabel}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-xs">{typeLabel}</span>
        )}
      </TableCell>
      <TableCell className="max-w-[180px] truncate text-xs font-mono text-muted-foreground">
        {tx.model_api_id ?? "\u2014"}
      </TableCell>
      <TableCell
        className={cn(
          "text-right text-sm font-medium tabular-nums",
          isDeduction ? "text-red-500 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"
        )}
      >
        {formatCredits(tx.amount_credits)}
      </TableCell>
    </TableRow>
  );
}

/* ────────────────── Main Export ─────────────────────────────────── */

export function UsageHistorySettings() {
  const { getToken, isSignedIn } = useAuth();
  const [page, setPage] = useState(1);
  const [projectFilter, setProjectFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Fetch transactions
  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      ...(projectFilter !== "all" ? { project_id: projectFilter } : {}),
      ...(typeFilter !== "all" ? { module_type: typeFilter } : {}),
    }),
    [page, projectFilter, typeFilter]
  );
  const { data, isLoading } = useTransactions(params);

  // Fetch projects for filter dropdown
  const { data: projectsData } = useQuery<PaginatedProjectList>({
    queryKey: ["projects-for-filter"],
    queryFn: () => {
      const api = createProjectsApi({ getToken });
      return api.list({ limit: 100, sortBy: "updated_at", sortOrder: "desc" });
    },
    enabled: !!isSignedIn,
    staleTime: 60_000,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const start = data ? (page - 1) * PAGE_SIZE + 1 : 0;
  const end = data ? Math.min(page * PAGE_SIZE, data.total) : 0;

  function handleFilterChange(setter: (v: string) => void) {
    return (value: string) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <div className="w-full space-y-6 p-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Usage history</h1>
        <p className="text-muted-foreground text-sm">
          Track how your credits are used across workflows.
        </p>
      </div>
      <Separator />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={projectFilter} onValueChange={handleFilterChange(setProjectFilter)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All workflows" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workflows</SelectItem>
            {projectsData?.items.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={handleFilterChange(setTypeFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            {TYPE_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Date</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="w-[90px] text-right">Credits</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton />
            ) : data && data.items.length > 0 ? (
              data.items.map((tx) => <TransactionRow key={tx.id} tx={tx} />)
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-muted-foreground h-32 text-center"
                >
                  No transactions yet. Run a workflow to see your usage history.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            Showing {start}\u2013{end} of {data.total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
              Previous
            </Button>
            <span className="text-muted-foreground text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
